<template>
  <!-- Desktop: no modal drawer — the same bottom-left pill grows in place
  (no backdrop, page stays fully interactive/scrollable around it), rather
  than jumping to a disconnected side panel. -->
  <!-- `v-if="!isDismissed"` (not `v-show`) so the `<Transition>` plays a
  real leave animation on dismiss — the whole panel (collapsed + expanded
  states alike) fades out and drops a few pixels rather than just vanishing. -->
  <Transition
    leave-active-class="transition duration-200 ease-out"
    leave-to-class="opacity-0 translate-y-8"
  >
    <div
      v-if="isDesktop && !isDismissed"
      class="fixed bottom-24 left-24 w-[380px] flex flex-col overflow-hidden rounded-lg bg-float-default-hi shadow-long transition-all duration-300 ease-out"
      :class="isOpen ? 'h-[calc(100vh-48px)] z-20' : 'h-[402px] z-[9]'"
      data-test="live-shopping-launcher"
    >
      <!-- Collapsed gets an explicit height (`402px`, measured against the
      real 6-product/2-deal curated list) instead of relying on natural/auto
      content sizing — CSS can't interpolate `height` to/from `auto`, so
      without this, `transition-all` above would just snap between states
      instead of animating. If the curated product count/copy changes enough
      to visibly under/overflow this, remeasure and update the value (a
      couple of px slack either way is fine — `overflow-hidden` on this same
      element only clips if content grows meaningfully taller). -->

      <!-- z-index is state-dependent, not a flat constant:
      - Collapsed (`z-[9]`, matches `WebSidebarShoppingAssistantFAB`): stays
        below the lead-collection team's popup (`z-10`), also bottom-anchored
        on Home/PLP/search on mobile — avoids visually dominating their
        conversion feature when both happen to render in the same corner.
      - Expanded (`z-20`): at `h-[calc(100vh-48px)]` + `bottom-24`, the
        panel's top edge sits ~24px from the viewport top regardless of
        window height — inside `TheHeader.vue`'s sticky `z-10` territory.
        `z-[9]` there got rendered *under* the header, clipping the panel's
        top row (Minimize/Dismiss buttons). The collapsed pill never reaches
        that high, so only the expanded state needs to clear the header. -->
      <LiveShoppingCollapsedPreview
        v-show="!isOpen"
        ref="collapsedPreviewRef"
        :is-desktop
        :product-count-label="productCountLabel"
        :products
        :title="i18n(translations.title)"
        :video-id="videoId"
        @add-to-cart="handleAddToCart"
        @dismiss="handleDismiss"
        @expand="handleOpen"
        @jump-to-moment="handleCollapsedJumpToMoment"
        @product-click="handleProductClick"
      />

      <!-- `v-if`, not `v-show`: this is the unmuted player (expanding is a
      user gesture, so unmuted autoplay is allowed). Mounting it while
      collapsed autoplays a hidden YouTube iframe with sound as soon as the
      document has any user activation (e.g. accepting the cookie banner). -->
      <div v-if="isOpen" class="flex h-full flex-col overflow-hidden">
        <div class="flex items-center justify-between p-16">
          <RevButtonIcon
            :alternative-text="i18n(translations.minimizeLabel)"
            :icon="IconMinus"
            size="small"
            variant="ghost"
            @click="handleClose"
          />
          <RevButtonIcon
            :alternative-text="i18n(translations.dismissLabel)"
            :icon="IconCrossInCircleFilled"
            size="small"
            variant="ghost"
            @click="handleDismiss"
          />
        </div>

        <LiveShoppingExpandedContent
          ref="expandedContentRef"
          class="flex-1 overflow-hidden"
          :product-count-label="productCountLabel"
          :products
          :status
          :title="i18n(translations.title)"
          :video-id="videoId"
          @add-to-cart="handleAddToCart"
          @jump-to-moment="handleJumpToMoment"
          @product-click="handleProductClick"
          @video-progress="handleVideoProgress"
          @video-start="trackVideoStarted"
        />
      </div>
    </div>
  </Transition>

  <!-- Mobile: full-screen drawer sheet (RevDrawer's default presentation
  below the `md` breakpoint) — this is what "expanding" means on mobile,
  unlike desktop's in-place growth above. `has-padding="false"` — padding is
  managed inside LiveShoppingExpandedContent.vue instead, so the video can
  sit edge-to-edge while the content below it stays padded. -->
  <RevDrawer
    v-if="!isDesktop"
    :has-padding="false"
    :name="MODAL_NAMES.LIVE_SHOPPING_OVERLAY"
    :title="i18n(translations.title)"
    @close="handleClose"
    @open="handleOpen"
  >
    <template #trigger="{ open }">
      <div v-show="!isOpen">
        <Transition
          leave-active-class="transition duration-200 ease-out"
          leave-to-class="opacity-0 translate-y-8"
        >
          <div
            v-if="!isDismissed && !isHiddenByLeadCollection"
            class="fixed bottom-24 left-24 z-[9] w-[160px] overflow-hidden rounded-lg bg-float-default-hi shadow-long"
            data-test="live-shopping-launcher"
          >
            <LiveShoppingCollapsedPreview
              ref="collapsedPreviewRef"
              :is-desktop
              :product-count-label="productCountLabel"
              :products
              :title="i18n(translations.title)"
              :video-id="videoId"
              @add-to-cart="handleAddToCart"
              @dismiss="handleDismiss"
              @expand="open"
              @jump-to-moment="handleCollapsedJumpToMoment"
              @product-click="handleProductClick"
            />
          </div>
        </Transition>
      </div>
    </template>

    <template #body>
      <LiveShoppingExpandedContent
        ref="expandedContentRef"
        :product-count-label="productCountLabel"
        :products
        :status
        :title="i18n(translations.title)"
        :video-id="videoId"
        @add-to-cart="handleAddToCart"
        @jump-to-moment="handleJumpToMoment"
        @product-click="handleProductClick"
        @video-progress="handleVideoProgress"
        @video-start="trackVideoStarted"
      />
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
} from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevDrawer } from '@ds/components/Drawer'
import { closeModal } from '@ds/components/ModalBase'
import { IconCrossInCircleFilled } from '@ds/icons/IconCrossInCircleFilled'
import { IconMinus } from '@ds/icons/IconMinus'
import { useMediaQuery } from '@vueuse/core'

import { useLeadCollectionPopupVisibility } from '../../lead-collection/composables/useLeadCollectionPopupVisibility'
import { useLiveShoppingPageContext } from '../composables/useIsLiveShoppingEnabled'
import {
  type LiveShoppingProduct,
  useLiveShoppingProducts,
} from '../composables/useLiveShoppingProducts'
import { useLiveShoppingTracking } from '../composables/useLiveShoppingTracking'
import { LIVE_SHOPPING_CONTENT, MODAL_NAMES } from '../constants'

import LiveShoppingCollapsedPreview from './LiveShoppingCollapsedPreview.vue'
import LiveShoppingExpandedContent from './LiveShoppingExpandedContent.vue'
import translations from './LiveShoppingWidget.translations'

const i18n = useI18n()

// Which video + curated product list this page gets (iPad pages → the EN
// iPads video, MacBook pages and Home → the EN MacBooks video). The widget
// only mounts when the bucket's flag is enabled (v-if in default.vue), so a
// context always exists — the fallback is purely for type safety.
const pageContext = useLiveShoppingPageContext()
const category = computed(() => pageContext.value?.category ?? 'macbook')

const videoId = computed(() => LIVE_SHOPPING_CONTENT[category.value].videoId)

const featuredProductsCount = computed(
  () => LIVE_SHOPPING_CONTENT[category.value].products.length,
)

// Pre-translated so the children don't each need the category: "5 iPads" on
// iPad pages, "4 MacBooks" on MacBook pages/Home.
const productCountLabel = computed(() =>
  i18n(
    category.value === 'ipad'
      ? translations.productCountIpad
      : translations.productCountMacbook,
    { count: featuredProductsCount.value },
  ),
)

// Mirrors the `md` Tailwind breakpoint (see useShoppingAssistantViewport.ts)
// — at/above it the widget grows in place instead of opening a drawer, and
// the collapsed pill previews the product list. `ssrWidth` gives VueUse a
// deterministic width to evaluate the query against outside the browser —
// this component is always mounted behind `<ClientOnly>` so it doesn't
// actually get server-rendered/hydrated, but this keeps `useMediaQuery`
// itself safe by construction rather than relying on that call-site detail.
const isDesktop = useMediaQuery('(min-width: 768px)', { ssrWidth: 390 })

// Lead-collection popup coexistence (mobile only): both features are
// bottom-anchored on the same Home/PLP/search pages. Hide ours only while
// theirs is *actually on screen* — not merely eligible, which would also
// hide us during their 10s pre-show delay and (because their persisted
// dismissal state isn't reactive) indefinitely after someone closes it.
// It fades out via the existing <Transition> and fades back in when theirs
// is dismissed. Desktop is unaffected (opposite corners).
const isLeadCollectionPopupDisplayed = useLeadCollectionPopupVisibility()
const isHiddenByLeadCollection = computed(
  () => !isDesktop.value && isLeadCollectionPopupDisplayed.value,
)

const { products, status, fetchOnce } = useLiveShoppingProducts(category)
const {
  trackOverlayOpened,
  trackOverlayClosed,
  trackOverlayDismissed,
  trackVideoStarted,
  trackVideoProgress,
  trackProductClicked,
  trackJumpToMomentClicked,
  trackAddToCart,
} = useLiveShoppingTracking()

const collapsedPreviewRef = useTemplateRef('collapsedPreviewRef')
const expandedContentRef = useTemplateRef('expandedContentRef')
const isOpen = ref(false)

// Set only while we close the mobile drawer ourselves because a product card
// is navigating away — see handleClose/handleProductClick.
let isClosingForProductNavigation = false

// sessionStorage, not localStorage — persists across page navigations for
// the lifetime of this browser session (cleared when the browser/tab is
// closed), not forever. Dismissing shouldn't bring the widget back on other
// pages visited later in the same session, but a genuinely new session
// should still see it.
const DISMISSED_STORAGE_KEY = 'liveShoppingDismissed'
const isDismissed = ref(
  sessionStorage.getItem(DISMISSED_STORAGE_KEY) === 'true',
)

onMounted(() => {
  // Fetched as soon as the widget mounts (not gated behind opening the
  // drawer) because the collapsed widget previews the product list on
  // desktop — see useLiveShoppingProducts.ts for why this must be
  // idempotent rather than gated on useAsyncData's own `status`. It's a
  // single lightweight curated-objectID Algolia lookup (3 products), so
  // this stays cheap even on the home bucket (see
  // useIsLiveShoppingEnabled.ts), which is higher-traffic than the
  // iPad-keyword-filtered PLP/search/PDP buckets.
  fetchOnce()

  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})

// A PDP card on desktop opens in a new tab, leaving this tab (and its
// player) running in the background. Pause whenever the tab is hidden —
// not just on card click — so switching tabs doesn't keep the video audible
// from a tab the visitor isn't looking at. Resume on return only when we're
// the ones who paused it (not a manual pause by the visitor).
let shouldResumeExpandedOnVisible = false
let shouldResumeCollapsedOnVisible = false

function handleVisibilityChange() {
  if (document.hidden) {
    shouldResumeExpandedOnVisible = expandedContentRef.value?.pause() ?? false
    shouldResumeCollapsedOnVisible = collapsedPreviewRef.value?.pause() ?? false

    return
  }

  if (shouldResumeExpandedOnVisible) expandedContentRef.value?.resume()
  if (shouldResumeCollapsedOnVisible) collapsedPreviewRef.value?.resume()

  shouldResumeExpandedOnVisible = false
  shouldResumeCollapsedOnVisible = false
}

async function handleOpen() {
  isOpen.value = true
  trackOverlayOpened()

  // Always restart from the beginning on expand rather than resuming. The
  // expanded player only mounts on open (see the `v-if` above), so this also
  // guarantees the unmuted player never exists while collapsed. `nextTick`
  // covers the moment the video ref appears — desktop mounts it via `v-if`,
  // mobile via the drawer's `#body` slot.
  await nextTick()
  expandedContentRef.value?.seekTo(0)
}

function handleClose() {
  isOpen.value = false

  // Closing because the card navigates away isn't the "dismissed the
  // overlay" signal the closed event is meant to measure — the product
  // click is already tracked on its own.
  if (isClosingForProductNavigation) {
    isClosingForProductNavigation = false
    return
  }

  trackOverlayClosed()
}

// Dismisses the collapsed widget entirely (until the next page load) without
// opening it — distinct from handleClose, which closes it after it was
// opened.
function handleDismiss() {
  isDismissed.value = true
  sessionStorage.setItem(DISMISSED_STORAGE_KEY, 'true')
  trackOverlayDismissed()
}

function handleProductClick(product: LiveShoppingProduct, position: number) {
  // On mobile the overlay is a full-screen drawer and the card navigates in
  // the same tab — close it explicitly, since the drawer lives in the layout
  // and would otherwise stay mounted over the new PDP. Desktop opens the PDP
  // in a new tab and keeps the in-place panel open, so it's desktop-only.
  if (!isDesktop.value && isOpen.value) {
    isClosingForProductNavigation = true
    closeModal(MODAL_NAMES.LIVE_SHOPPING_OVERLAY)
  }

  trackProductClicked(product, position)
}

function handleAddToCart(product: LiveShoppingProduct, position: number) {
  trackAddToCart(product, position)
}

function handleVideoProgress(percent: number) {
  trackVideoProgress(percent)
}

function handleJumpToMoment(product: LiveShoppingProduct, position: number) {
  expandedContentRef.value?.seekTo(product.timestampSeconds)
  trackJumpToMomentClicked(product, position)
}

function handleCollapsedJumpToMoment(
  product: LiveShoppingProduct,
  position: number,
) {
  collapsedPreviewRef.value?.seekTo(product.timestampSeconds)
  trackJumpToMomentClicked(product, position)
}
</script>
